import React, { useState, useRef, useEffect, useCallback } from 'react';
import config from '../../config';
import { InputNumber } from 'primereact/inputnumber';
import noimage from '../../assets/images/Desktop placeholder.svg';
import styles from './Tenthouseaccordian.module.css'; // Create this CSS module

const Tenthouseaccordian = ({ serviceData, onNext }) => {
  const [addedItems, setAddedItems] = useState([]);
  const [currentIndexes, setCurrentIndexes] = useState({});
  const [imageLoading, setImageLoading] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);

  const hasAddedItems = addedItems.some(item => item.checked && item.count > 0);

  // Simulate data loading
  useEffect(() => {
    if (serviceData && serviceData.length > 0) {
      setDataLoading(false);
      // Set first category as selected by default
      if (serviceData.length > 0 && !selectedCategory) {
        setSelectedCategory(serviceData[0].id);
      }
    }
  }, [serviceData]);

  const handleNextImage = useCallback((service) => {
    setImageLoading((prev) => ({ ...prev, [service.id]: true }));
    setCurrentIndexes((prev) => ({
      ...prev,
      [service.id]:
        prev[service.id] === undefined
          ? 1
          : (prev[service.id] + 1) % service.gallery.length,
    }));
  }, []);

  const handlePrev = useCallback((service) => {
    setImageLoading((prev) => ({ ...prev, [service.id]: true }));
    setCurrentIndexes((prev) => ({
      ...prev,
      [service.id]:
        prev[service.id] === undefined || prev[service.id] === 0
          ? service.gallery.length - 1
          : prev[service.id] - 1,
    }));
  }, []);

  const handleCheckboxChange = useCallback((service, checked) => {
    setAddedItems((prev) => {
      const exists = prev.find((item) => item.id === service.id);
      if (exists) {
        return prev.map((item) =>
          item.id === service.id
            ? { ...item, checked, count: checked ? item.count || 1 : 0 }
            : item
        );
      } else {
        return [...prev, { ...service, checked, count: 1 }];
      }
    });
  }, []);

  const MAX_COUNT = 99999;

  const handleCountChange = useCallback((serviceId, count) => {
    const safeCount = Math.min(count, MAX_COUNT);
    setAddedItems((prev) => {
      const exists = prev.find((item) => item.id === serviceId);
      if (exists) {
        return prev.map((item) =>
          item.id === serviceId
            ? { ...item, count: safeCount, checked: safeCount > 0 }
            : item
        );
      } else {
        let service = null;
        for (const cat of serviceData) {
          const found = cat.services?.find((s) => s.id === serviceId);
          if (found) {
            service = found;
            break;
          }
        }
        if (!service) return prev;
        return [...prev, { ...service, count: safeCount, checked: safeCount > 0 }];
      }
    });
  }, [serviceData]);

  const handleNext = useCallback(() => {
    const filtered = addedItems
      .filter((item) => item.checked && item.count > 0)
      .map((item) => ({
        service_name: item.service_name,
        count: item.count,
        id: item.id,
      }));

    console.log("Selected Services:", filtered);
    if (onNext) {
      onNext(filtered);
    }
  }, [addedItems, onNext]);

  // Filter services based on search term
  const filteredServiceData = serviceData
    ?.map((category) => ({
      ...category,
      services: category.services?.filter((service) =>
        service.service_name?.toLowerCase().includes(searchTerm.toLowerCase())
      ) || [],
    }))
    .filter((category) => category.services?.length > 0) || [];

  // Get first image from category for display
  const getCategoryImage = useCallback((category) => {
    const firstService = category.services?.[0];
    return firstService?.gallery?.[0];
  }, []);

  // Get services for selected category with search filter
  const getServicesForSelectedCategory = useCallback(() => {
    const category = filteredServiceData.find(cat => cat.id === selectedCategory);
    if (!category) return [];
    
    return category.services.filter(service =>
      service.service_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [filteredServiceData, selectedCategory, searchTerm]);

  // Show loading spinner while data is loading
  if (dataLoading) {
    return (
      <div className={styles.container}>
        <h3 style={{ textAlign: "center" }}>
          <i
            className="pi pi-spin pi-spinner-dotted"
            style={{ fontSize: "2rem", color: "#0cc0df" }}
          ></i>
        </h3>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Search Bar */}
      <div className={styles.searchContainer}>
        <input
          type="text"
          placeholder="Search Items..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={styles.searchInput}
        />
      </div>

      {/* Two Column Layout */}
      <div className={styles.twoColumnLayout}>
        {/* Left Sidebar - Categories */}
        <div className={styles.categorySidebar}>
          <h3 className={styles.categoriesTitle}>Categories</h3>
          <div className={styles.categoriesList}>
            {filteredServiceData.length === 0 ? (
              <p className={styles.noData}>No categories available</p>
            ) : (
              filteredServiceData.map((category) => {
                const firstImage = getCategoryImage(category);
                const serviceCount = category.services?.length || 0;
                
                return (
                  <div
                    key={category.id}
                    className={`${styles.categoryItem} ${selectedCategory === category.id ? styles.activeCategory : ''}`}
                    onClick={() => setSelectedCategory(category.id)}
                  >
                    <div className={styles.categoryImageContainer}>
                      {firstImage?.id ? (
                        <img
                          src={`${config.API_BASE_URL}/gallery/download/${firstImage.id}`}
                          alt={category.name}
                          className={styles.categoryImage}
                          onError={(e) => { e.target.src = noimage; }}
                        />
                      ) : (
                        <img
                          src={noimage}
                          alt="No image"
                          className={styles.categoryImage}
                        />
                      )}
                    </div>
                    <div className={styles.categoryInfo}>
                      <div className={styles.categoryName}>{category.name}</div>
                      <div className={styles.serviceCount}>({serviceCount})</div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right Content - Services */}
        <div className={styles.servicesContent}>
          {selectedCategory && (
            <>
              {/* Category Header */}
              {(() => {
                const currentCategory = filteredServiceData.find(cat => cat.id === selectedCategory);
                const servicesCount = getServicesForSelectedCategory().length;
                return (
                  <div className={styles.categoryHeader}>
                    <div className={styles.categoryHeaderTitle}>
                      <span className={styles.selectedCategoryName}>
                        {currentCategory?.name || 'Services'}
                      </span>
                      <span className={styles.serviceCountBadge}>({servicesCount})</span>
                    </div>
                    <span className={styles.categoryHeaderSubtitle}>
                      Please select items and quantity below.
                    </span>
                  </div>
                );
              })()}

              {/* Services Grid - 4 columns */}
              <div className={styles.servicesGridContainer}>
                {getServicesForSelectedCategory().length === 0 ? (
                  <p className={styles.noData}>No services available in this category</p>
                ) : (
                  <div className={styles.servicesGrid}>
                    {getServicesForSelectedCategory().map((service) => {
                      const selected = addedItems.find((item) => item.id === service.id);
                      const currentIndex = typeof currentIndexes[service.id] === "number" 
                        ? currentIndexes[service.id] 
                        : 0;
                      const currentImage = service.gallery && service.gallery.length > 0
                        ? service.gallery[currentIndex]
                        : null;
                      const loading = typeof imageLoading[service.id] === "boolean" 
                        ? imageLoading[service.id] 
                        : true;
                      const hasMultipleImages = service.gallery && service.gallery.length > 1;

                      return (
                        <div className={styles.serviceCard} key={service.id}>
                          <div className={styles.serviceImageContainer}>
                            {/* Loader */}
                            {loading && (
                              <div className={styles.imageLoader}>
                                <i
                                  className="pi pi-spin pi-spinner-dotted"
                                  style={{ fontSize: "2rem", color: "#0cc0df" }}
                                ></i>
                              </div>
                            )}

                            {/* Image */}
                            {currentImage?.id ? (
                              <img
                                src={`${config.API_BASE_URL}/gallery/download/${currentImage.id}`}
                                alt={service.service_name}
                                className={styles.serviceImage}
                                onLoad={() => setImageLoading((prev) => ({ ...prev, [service.id]: false }))}
                                onError={(e) => {
                                  setImageLoading((prev) => ({ ...prev, [service.id]: false }));
                                  e.target.src = noimage;
                                }}
                                style={{ display: loading ? "none" : "block" }}
                              />
                            ) : (
                              <img
                                src={noimage}
                                alt="No image"
                                className={styles.serviceImage}
                                style={{ display: loading ? "none" : "block" }}
                              />
                            )}

                            {/* Navigation Arrows */}
                            {hasMultipleImages && currentImage?.id && (
                              <>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlePrev(service);
                                  }}
                                  className={`${styles.navArrow} ${styles.leftArrow}`}
                                  disabled={loading}
                                >
                                  ‹
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleNextImage(service);
                                  }}
                                  className={`${styles.navArrow} ${styles.rightArrow}`}
                                  disabled={loading}
                                >
                                  ›
                                </button>
                              </>
                            )}

                            {/* Image Counter */}
                            {hasMultipleImages && currentImage?.id && (
                              <div className={styles.imageCounter}>
                                {currentIndex + 1} / {service.gallery.length}
                              </div>
                            )}
                          </div>

                          <div className={styles.serviceInfo}>
                            <div className={styles.serviceName}>
                              <label>{service.service_name}</label>
                            </div>

                            <div className={styles.counterWrapper}>
                              <div className={styles.counterBox}>
                                <button
                                  className={styles.counterBtn}
                                  onClick={() => handleCountChange(service.id, Math.max(0, (selected?.count || 0) - 1))}
                                  disabled={!selected || selected.count <= 0}
                                >
                                  −
                                </button>
                                <input
                                  type="number"
                                  className={styles.counterValue}
                                  value={selected?.count || 0}
                                  onChange={(e) => {
                                    const newValue = Math.min(Math.max(0, Number(e.target.value) || 0), 1000);
                                    handleCountChange(service.id, newValue);
                                  }}
                                  min={0}
                                  max={1000}
                                />
                                <button
                                  className={styles.counterBtn}
                                  onClick={() => handleCountChange(service.id, (selected?.count || 0) + 1)}
                                >
                                  +
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Next Button */}
      <div className={styles.nextButtonContainer}>
        <button
          className={`${styles.customButton} ${styles.saveButton} ${!hasAddedItems ? styles.buttonDisabled : ''}`}
          onClick={handleNext}
          disabled={!hasAddedItems}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default React.memo(Tenthouseaccordian);